import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import {
  Stack,
  IconButton,
  Typography,
  Popover,
  List,
  ListItem,
  Box,
  ListItemText,
  Badge,
  Button,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../../store/store';
import { Close } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';
import { fetchNotes } from '../../../store/slices/notifications';
import { useReva } from '../../../contexts/RevaContext';
import { StyledLink } from '../../StyledLink';
import { useLocation } from 'react-router-dom';
import { selectNotesForToolbar } from '../../../store/selectors/notes';
import notificationsService from '../../../api/services/notifications';
import { useSocket } from '../../../api/socket';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchMessages } from '../../../store/slices/messages';
import { fetchOffers } from '../../../store/slices/offers';
import { fetchShowings } from '../../../store/slices/showings';
import { notifications } from '@prisma/client';
import { fetchInspections } from '../../../store/slices/inspections';
import useUnreadCount from '../../../hooks/useUnreadCount';

interface Props {
  /**
   * Render the Ask Reva chip beside the bell. On desktop the rail's Reva tile
   * is the entry point; on a phone the sheet is closed, so the header carries it.
   */
  showReva: boolean;
}

/** The right-hand cluster of the app header: Ask Reva (phones), the bell and its popover. */
const MainButtons: React.FC<Props> = ({ showReva }) => {
  const notes = useSelector((state: RootState) => selectNotesForToolbar(state));
  const unreadCount = useUnreadCount();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const {
    openReva,
    available: revaAvailable,
    unseenCount: revaUnseenCount,
  } = useReva();
  const { socket, connect } = useSocket();
  const { currentUser } = useAuth();
  const isPBuyer = currentUser?.type === 'PBUYER';

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const markNotificationsRead = async () => {
    notes
      .filter((note) => note.read === false)
      .map((note) => {
        notificationsService.patch({
          id: note.id,
          read: true,
        });
      });
  };

  useEffect(() => {
    dispatch(fetchNotes());
    if (open) {
      markNotificationsRead();
    }
  }, [open]);

  useEffect(() => {
    connect();
  }, []);

  useEffect(() => {
    socket.on('messages created', () => {
      dispatch(fetchNotes());
    });
    socket.on('notifications created', () => {
      dispatch(fetchNotes());
      dispatch(fetchInspections());
    });
  }, [dispatch]);

  useEffect(() => {
    handleClose();
  }, [location]);

  const handleNotificationClick = (
    e: React.MouseEvent,
    notification: notifications
  ) => {
    if (isPBuyer) {
      e.preventDefault();
      dispatch(fetchMessages());
      dispatch(fetchShowings());
      dispatch(fetchOffers());
      handleClose();

      if (notification.resourceType === 'messages') {
        const element = document.getElementById('messages');
        element?.scrollIntoView({ behavior: 'auto', block: 'center' });
      }
      return;
    }

    if (!notification.link) {
      e.preventDefault();
    }
  };

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {showReva && location.pathname !== '/home' && revaAvailable && (
        <Button
          onClick={() => openReva()}
          size="small"
          startIcon={
            <Badge
              data-testid="reva-toolbar-badge"
              badgeContent={revaUnseenCount}
              overlap="circular"
              sx={{
                '& .MuiBadge-badge': {
                  color: 'black',
                  backgroundColor: 'highlight.main',
                },
              }}
            >
              <img
                src="/reva-icon-colored.png"
                alt=""
                style={{ width: 20, height: 20 }}
              />
            </Badge>
          }
          sx={{
            color: 'primary.dark',
            backgroundColor: 'rgba(39,154,241,0.10)',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.85rem',
            px: 1.25,
            borderRadius: 2,
            '&:hover': { backgroundColor: 'rgba(39,154,241,0.18)' },
          }}
        >
          Ask Reva
        </Button>
      )}
      <IconButton
        onClick={handleClick}
        data-testid="notifications-button"
        aria-label="Notifications"
        sx={{ color: 'text.secondary' }}
      >
        <Badge
          data-testid="notifications-badge"
          badgeContent={unreadCount}
          sx={{
            '& .MuiBadge-badge': {
              color: 'black',
              backgroundColor: 'highlight.main',
            },
          }}
        >
          <NotificationsNoneIcon color="inherit" />
        </Badge>
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
      >
        <Box p={2} width={'100%'} sx={{ maxHeight: '80vh' }}>
          <Stack
            direction="row"
            justifyContent={'space-between'}
            alignItems={'center'}
            sx={{
              position: 'sticky',
              top: 0,
              bgcolor: 'background.paper',
              zIndex: 1,
              pb: 1,
            }}
          >
            <Typography variant="body1" fontWeight={600}>
              Notifications
            </Typography>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Stack>
          <List sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            {notes.length === 0 && (
              <Typography>You have no notifications</Typography>
            )}
            {notes.map((notification) => (
              <StyledLink
                to={notification.link ?? ''}
                key={notification.id}
                style={{ textDecoration: 'none' }}
                onClick={(e) => handleNotificationClick(e, notification)}
              >
                <ListItem key={notification.id} sx={{ p: 0 }}>
                  <ListItemText
                    primary={notification.text}
                    primaryTypographyProps={{
                      variant: 'body2',
                    }}
                    secondary={new Date(
                      notification.createdAt
                    ).toLocaleString(undefined, {
                      dateStyle: 'long',
                      timeStyle: 'short',
                    })}
                    secondaryTypographyProps={{
                      variant: 'caption',
                    }}
                  />
                </ListItem>
              </StyledLink>
            ))}
          </List>
        </Box>
      </Popover>
    </Stack>
  );
};

export default MainButtons;
